import React, { useState, useEffect } from 'react';
import useSWR, { mutate } from 'swr';
import { getUsersUrl, deleteUser, unlockUser,lockUser, fetcher } from '../../../api/ApiUser';
// import { getUsersUrl, deleteUser, unlockUser, addUser, updateUser, lockUser, fetcher } from '../../../pages/Dashboard/ApiUser';
import Header from '../../../pages/Dashboard/Header';
import SidebarApp from '../Roles/Modals/SidebarApp';
import { useParams } from 'react-router-dom';
import UserList from './UserList';
import UserFormModal from './UserFormModal';
import Notification from '../Users/Sharerd/Notification';
import { useTranslation } from '../../../../TranslationContext';
import Navbar0 from '../../../../vitrine/Components/Navbar';

interface User {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
  isLocked?: boolean;
  status: string;
  lastSuccessfull: string;
  failureResponse: string;
  lastActivity?: string;
}

const Users: React.FC = () => {
  const { t } = useTranslation();
  const { appId } = useParams<{ appId: string }>();
  const companyId = localStorage.getItem('company');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  if (!companyId || !appId) {
    return <div className="p-4 text-red-500">{t('missing_company_or_app_id')}</div>;
  }

  const { data: usersData, error, isValidating } = useSWR<{ isSuccess: boolean; data: User[]; errorMessage: string }>(
    getUsersUrl(companyId, appId),
    fetcher,
    { revalidateOnFocus: true }
  );
  // console.log('UserData',usersData)
  const users = usersData?.data || [];
  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.fullName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const refreshUsers = () => mutate(getUsersUrl(companyId, appId));

  const handleUserAction = async (
    action: 'delete' | 'lock' | 'unlock',
    userId: string,
    successMessage: string,
    errorMessage: string
  ) => {
    try {
      switch (action) {
        case 'delete':
          await deleteUser(companyId, appId, userId);
          break;
        case 'lock':
          await lockUser(companyId, appId, userId);
          break;
        case 'unlock':
          await unlockUser(companyId, appId, userId);
          break;
      }
      setNotification({ message: successMessage, type: 'success' });
      refreshUsers();
    } catch (error) {
      setNotification({ message: errorMessage, type: 'error' });
    }
  };

  const handleSuccess = (message: string) => {
    setNotification({ message, type: 'success' });
    refreshUsers();
  };

  const handleError = (message: string) => {
    setNotification({ message, type: 'error' });
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <div className="flex flex-col h-screen">
      <Navbar0/>
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <SidebarApp />
        <main className="flex-1 overflow-y-auto p-6">
          {notification && (
            <Notification 
              message={notification.message} 
              type={notification.type} 
            />
          )}

          <UserList
            users={filteredUsers}
            loading={isValidating && !usersData}
            error={error}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onAddUser={() => {
              setSelectedUser(null);
              setIsFormOpen(true);
            }}
            onEditUser={(user) => {
              setSelectedUser(user);
              setIsFormOpen(true);
            }}
            onDeleteUser={(userId) => handleUserAction(
              'delete',
              userId,
              t('user_deleted_successfully'),
              t('error_deleting_user')
            )}
            onLockUser={(userId) => handleUserAction(
              'lock',
              userId,
              t('user_locked_successfully'),
              t('error_locking_user')
            )}
            onUnlockUser={(userId) => handleUserAction(
              'unlock',
              userId,
              t('user_unlocked_successfully'),
              t('error_unlocking_user')
            )}
          />
          
          <UserFormModal
            open={isFormOpen}
            user={selectedUser}
            companyId={companyId}
            applicationId={appId}
            onClose={() => setIsFormOpen(false)}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        </main>
      </div>
    </div>
  );
};

export default Users;
